import { useState, useEffect } from "react";
import { v4 as uuid } from "uuid";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../../../api/axios";
import FormContentManager from "../../../components/common/form/FormContentManager";
import TableCell from "../../../components/common/ui/TableCell";
import { getMaterialDoc } from "../../../api/material/meta";
import PeopleCellLoader from "../../../components/common/people/PeopleCellLoader";

import { addApprovalLine } from "../../../api/approval";

export default function Confirm_new() {
  const { id: projectId, dns: paramDns } = useParams();
  const [searchParams] = useSearchParams();

  /* ===============================
     로그인 유저
  =============================== */
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const myId = String(user.id);

  const [dns, setDns] = useState(paramDns || null);

  /* ===============================
     QC 연결
  =============================== */
  const initQcDns = searchParams.get("qc_dns") || "";
  const [qcDns, setQcDns] = useState(initQcDns);
  const [qcData, setQcData] = useState(null);

  const [qcList, setQcList] = useState([]);

  /* ===============================
     담당자 판단
  =============================== */
  const assignedPeople = qcData?.header?.assigned_people || [];
  const constructorId = String(assignedPeople[0] || "");
  const pmId = String(assignedPeople[1] || "");

  // 🔥 새 CK 문서 작성 시(!dns)에는 누구나 시공사 체크 가능
  const isNewDoc = !dns;
  const canCheckConstructor = isNewDoc || myId === constructorId;
  const canCheckPM = myId === pmId;
  const canAssignNext = myId === constructorId && !pmId;

  /* ===============================
     다음 담당자
  =============================== */
  const [nextAssignee, setNextAssignee] = useState([]);

  /* ===============================
     기본 정보
  =============================== */
  const [title, setTitle] = useState("검수 체크리스트");
  const [writer, setWriter] = useState("");
  const [writeDate, setWriteDate] = useState("");
  const [checkDate, setCheckDate] = useState("");

  /* ===============================
     공종 정보
  =============================== */
  const [codeNo, setCodeNo] = useState("");
  const [workType, setWorkType] = useState("");
  const [locationPart, setLocationPart] = useState("");
  const [subWorkType, setSubWorkType] = useState("");
  const [workAmount, setWorkAmount] = useState("");

  const divisionOptions = [
    "건축",
    "구조",
    "토목",
    "설비",
    "전기",
    "소방",
    "의장",
    "기타",
  ];

  useEffect(() => {
    setWriter(user.name || "");
    setWriteDate(new Date().toISOString().slice(0, 10));
  }, []);

  /* ===============================
     QC 목록
  =============================== */
  useEffect(() => {
    (async () => {
      const res = await api.get("material/docs/", {
        params: {
          project: projectId,
          form_type: "QC",
          page_size: 100,
          ordering: "-created_at",
        },
      });

      const results = res.data?.results || [];
      const filtered = results.filter((q) => {
        const assigned = q?.header?.assigned_people || [];
        return assigned.includes(user.id);
      });

      setQcList(filtered);
    })();
  }, [projectId]);

  /* ===============================
     QC 문서 로드
  =============================== */
  const loadQcDoc = async (dnsValue) => {
    if (!dnsValue) {
      setQcDns("");
      setQcData(null);
      return;
    }

    const res = await getMaterialDoc({
      dns: dnsValue,
      project: projectId,
    });

    setQcDns(dnsValue);
    setQcData(res);
  };

  useEffect(() => {
    if (initQcDns) loadQcDoc(initQcDns);
  }, [initQcDns]);

  /* ===============================
     체크리스트
  =============================== */
  const makeRow = () => ({
    _id: uuid(),
    item: "",
    criteria: "",
    s_ok: false,
    s_ng: false,
    pm_ok: false,
    pm_ng: false,
    action: "",
  });

  const [rows, setRows] = useState(Array.from({ length: 10 }, makeRow));

  const updateCell = (id, key, value) =>
    setRows((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  const addRow = () => setRows((p) => [...p, makeRow()]);
  const removeRow = (id) =>
    setRows((p) => (p.length > 1 ? p.filter((r) => r._id !== id) : p));

  /* ===============================
     SUMMARY
  =============================== */
  const summaryConfig = {
    title: "검수 체크리스트 요약",
    sections: [
      { label: "제목", value: title },
      { label: "작성자", value: writer },
      { label: "작성일자", value: writeDate },
      { label: "검측일자", value: checkDate },
      { label: "연결된 검수 요청서", value: qcDns || "-" },
    ],
  };

  /* ===============================
     SAVE BODY
     🔥 CK는 전자결재 문서 → level2 = APR
  =============================== */
  const getBody = () => {
    const cleanLines = rows
      .filter((r) => Object.values(r).some((v) => v && v !== false))
      .map(({ _id, ...rest }) => rest);

    // ⭐ 시공사 불합격 항목 확인 - rows에서 직접 체크 (cleanLines가 아닌)
    const hasConstructorFailure = rows.some((r) => r.s_ng === true);

    console.log("🔍 [getBody] 시공사 불합격 확인:", {
      hasConstructorFailure,
      rowsWithSng: rows.filter((r) => r.s_ng === true),
      allRows: rows.map((r) => ({ item: r.item, s_ok: r.s_ok, s_ng: r.s_ng })),
      cleanLines,
      willSetStatus: hasConstructorFailure ? "rejected" : "none (기본값)",
    });

    const finalConstructorId = constructorId || null;
    const finalPmId =
      nextAssignee?.[0]?.id || qcData?.header?.assigned_people?.[1] || null;

    const payload = {
      values: {
        project: projectId,
        module: "material",
        doc_type: "CK",
        title,

        // 🔥 시공사 불합격이 있으면 처음부터 rejected 상태로 생성
        status: hasConstructorFailure ? "rejected" : undefined,

        header: {
          writer,
          write_date: writeDate,
          check_date: checkDate,
          qc_dns: qcDns,
          work_type: workType,
          code_no: codeNo,
          location_part: locationPart,
          sub_work_type: subWorkType,
          work_amount: workAmount,
          assigned_people:
            finalConstructorId && finalPmId
              ? [finalConstructorId, finalPmId]
              : finalConstructorId
              ? [finalConstructorId]
              : [],
        },

        lines: cleanLines,

        // ✅ 전자결재 문서
        level2: "APR",
      },
    };

    console.log("📦 [getBody] 최종 payload:", payload);
    console.log("📦 [getBody] payload.values.status:", payload.values.status);

    return payload;
  };

  return (
    <FormContentManager
      params={{ module: "material", project: projectId, dns }}
      getBody={getBody}
      onChangeId={setDns}
      redirectTo={`/project/${projectId}/material/materialboard`}
      docName="검수 체크리스트"
      docDescribe="검수 체크리스트를 작성하세요."
      summaryConfig={summaryConfig}
      onSaved={async ({ dns: savedDns }) => {
        console.log("📝 CK 저장 완료:", { savedDns, qcDns });

        try {
          // ⭐ 1. 시공사 불합격 항목 확인 - rows에서 직접 체크!
          const hasConstructorFailure = rows.some((r) => r.s_ng === true);

          console.log("🔍 [onSaved] 시공사 불합격 항목 확인:", {
            hasConstructorFailure,
            rowsWithSng: rows
              .filter((r) => r.s_ng === true)
              .map((r) => ({ item: r.item, s_ng: r.s_ng })),
            allRows: rows.map((r) => ({
              item: r.item,
              s_ok: r.s_ok,
              s_ng: r.s_ng,
            })),
          });

          // ⭐ 2. 시공사 불합격이 있으면 QC + CK 모두 반려
          if (hasConstructorFailure) {
            console.log("🚨 시공사 불합격 감지 → QC + CK 반려 처리");

            // 2-1. QC 문서 반려
            if (qcDns) {
              console.log(`🚨 QC 문서 반려 처리: ${qcDns}`);
              await api.patch(`papers/docs/${qcDns}/status/`, {
                status: "rejected",
              });
              console.log("✅ QC 문서 반려 완료");
            }

            // 2-2. CK 문서도 반려 (명시적 처리)
            if (savedDns) {
              console.log(`🚨 CK 문서 반려 처리: ${savedDns}`);
              await api.patch(`papers/docs/${savedDns}/status/`, {
                status: "rejected",
              });
              console.log("✅ CK 문서 반려 완료");
            }

            alert(
              "시공사 불합격 항목이 있어 검수 요청서와 체크리스트가 반려되었습니다.\n재작성 후 다시 요청해주세요."
            );
          }

          // ⭐ 3. PM 결재선 등록
          const finalPmId =
            nextAssignee?.[0]?.id || qcData?.header?.assigned_people?.[1];

          if (savedDns && finalPmId) {
            await addApprovalLine(
              {
                doc_dns: savedDns,
                approver_id: finalPmId,
                order: 1,
                refresh: true,
              },
              projectId
            );
            console.log("✅ PM 결재선 등록 완료");
          }
        } catch (err) {
          console.error("❌ onSaved 에러:", err);
          console.error("에러 상세:", err.response?.data || err.message);
        }
      }}
    >
      {/* 제목 */}
      <div className="text-center mb-6">
        <div className="text-xl font-bold">{title}</div>
      </div>

      {/* 작성 정보 */}
      <table className="doc-table w-full text-sm mb-4">
        <tbody>
          <tr>
            <TableCell
              label="작성자"
              editing
              input={
                <input
                  readOnly
                  className="input-base bg-gray-100"
                  value={writer}
                />
              }
            />
            <TableCell
              label="작성일자"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={writeDate}
                  onChange={(e) => setWriteDate(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="검수 요청서"
              colSpan={3}
              editing
              input={
                <select
                  className="input-base text-xs"
                  value={qcDns || ""}
                  onChange={(e) => {
                    const selectedDns = e.target.value;
                    setQcDns(selectedDns);
                    if (selectedDns) loadQcDoc(selectedDns);
                  }}
                >
                  <option value="">검수 요청서 선택</option>
                  {qcList.map((q) => (
                    <option key={q.dns} value={q.dns}>
                      {q.title} ({q.dns})
                    </option>
                  ))}
                </select>
              }
            />
          </tr>

          <tr>
            <TableCell
              label="제목"
              colSpan={3}
              editing
              input={
                <input
                  className="input-base"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {/* 공종 정보 */}
      <table className="doc-table w-full text-sm mb-6">
        <tbody>
          <tr>
            <TableCell
              label="공종 Code"
              editing
              input={
                <input
                  className="input-base"
                  value={codeNo}
                  onChange={(e) => setCodeNo(e.target.value)}
                />
              }
            />
            <TableCell
              label="검측 일자"
              editing
              input={
                <input
                  type="date"
                  className="input-base"
                  value={checkDate}
                  onChange={(e) => setCheckDate(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="공 종"
              editing
              input={
                <select
                  className="input-base text-xs"
                  value={workType}
                  onChange={(e) => setWorkType(e.target.value)}
                >
                  <option value="">선택</option>
                  {divisionOptions.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              }
            />
            <TableCell
              label="위치 및 부위"
              editing
              input={
                <input
                  className="input-base"
                  value={locationPart}
                  onChange={(e) => setLocationPart(e.target.value)}
                />
              }
            />
          </tr>

          <tr>
            <TableCell
              label="세부 공종"
              editing
              input={
                <input
                  className="input-base"
                  value={subWorkType}
                  onChange={(e) => setSubWorkType(e.target.value)}
                />
              }
            />
            <TableCell
              label="공사량"
              editing
              input={
                <input
                  className="input-base"
                  value={workAmount}
                  onChange={(e) => setWorkAmount(e.target.value)}
                />
              }
            />
          </tr>
        </tbody>
      </table>

      {canAssignNext && (
        <table className="doc-table w-full text-sm mb-4">
          <tbody>
            <tr>
              <TableCell
                label="다음 담당자 (PM)"
                colSpan={3}
                editing
                input={
                  <PeopleCellLoader
                    single
                    dns={qcDns}
                    value={nextAssignee}
                    onChange={setNextAssignee}
                  />
                }
              />
            </tr>
          </tbody>
        </table>
      )}
      {/* 체크리스트 */}
      <div className="text-body-bold mb-2">검사 항목</div>

      <table className="doc-table w-full text-sm">
        <thead className="doc-thead">
          <tr>
            <th className="doc-th" rowSpan={3}>
              검사항목
            </th>
            <th className="doc-th" rowSpan={3}>
              검사기준
            </th>
            <th className="doc-th" colSpan={4}>
              검사 결과
            </th>
            <th className="doc-th" rowSpan={3}>
              조치사항
            </th>
            <th className="doc-th-end" rowSpan={3} />
          </tr>
          <tr>
            <th className="doc-th-result" colSpan={2}>
              시공사
            </th>
            <th className="doc-th-result" colSpan={2}>
              담당 PM
            </th>
          </tr>
          <tr>
            <th className="doc-th-result">합격</th>
            <th className="doc-th-result">불합격</th>
            <th className="doc-th-result">합격</th>
            <th className="doc-th-result">불합격</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => (
            <tr key={r._id}>
              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1"
                  value={r.item}
                  onChange={(e) => updateCell(r._id, "item", e.target.value)}
                />
              </td>
              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1"
                  value={r.criteria}
                  onChange={(e) =>
                    updateCell(r._id, "criteria", e.target.value)
                  }
                />
              </td>

              {/* 시공사 */}
              <td className="doc-td text-center">
                <input
                  type="checkbox"
                  checked={r.s_ok}
                  disabled={!canCheckConstructor}
                  onChange={() => {
                    if (!canCheckConstructor) return;
                    updateCell(r._id, "s_ok", !r.s_ok);
                    updateCell(r._id, "s_ng", false);
                  }}
                />
              </td>
              <td className="doc-td text-center">
                <input
                  type="checkbox"
                  checked={r.s_ng}
                  disabled={!canCheckConstructor}
                  onChange={() => {
                    if (!canCheckConstructor) return;
                    updateCell(r._id, "s_ng", !r.s_ng);
                    updateCell(r._id, "s_ok", false);
                  }}
                />
              </td>

              {/* PM */}
              <td className="doc-td text-center">
                <input
                  type="checkbox"
                  checked={r.pm_ok}
                  disabled={!canCheckPM}
                  onChange={() => {
                    if (!canCheckPM) return;
                    updateCell(r._id, "pm_ok", !r.pm_ok);
                    updateCell(r._id, "pm_ng", false);
                  }}
                />
              </td>
              <td className="doc-td text-center">
                <input
                  type="checkbox"
                  checked={r.pm_ng}
                  disabled={!canCheckPM}
                  onChange={() => {
                    if (!canCheckPM) return;
                    updateCell(r._id, "pm_ng", !r.pm_ng);
                    updateCell(r._id, "pm_ok", false);
                  }}
                />
              </td>

              <td className="doc-td p-0">
                <textarea
                  className="input-sm px-2 py-1"
                  value={r.action}
                  onChange={(e) => updateCell(r._id, "action", e.target.value)}
                />
              </td>

              <td className="doc-td text-center">
                <button className="doc-delete" onClick={() => removeRow(r._id)}>
                  삭제
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2">
        <button className="btn-basic-sm" onClick={addRow}>
          + 검사 항목 추가
        </button>
      </div>
    </FormContentManager>
  );
}
